import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { X, CheckCircle } from "lucide-react";
import CustomAuthForm from "./CustomAuthForm";
import { useAuth } from "@/contexts/AuthContext";
import { ReportPreviewCard } from "./ReportPreviewCard";

interface SignUpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSignUpSuccess: () => void;
}

export function SignUpModal({
  isOpen,
  onClose,
  onSignUpSuccess,
}: SignUpModalProps) {
  const { user } = useAuth();

  // Watch for authentication state changes
  useEffect(() => {
    if (user && isOpen) {
      // User has successfully signed up/in, trigger success callback
      onSignUpSuccess();
    }
  }, [user, isOpen, onSignUpSuccess]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <Card className="w-full max-w-5xl my-8">
        <CardHeader className="relative pb-4">
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-4 top-4"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>

          <div className="text-center max-w-2xl mx-auto">
            <CardTitle className="text-3xl mb-3">Bine ați venit!</CardTitle>
            <p className="text-muted-foreground text-lg">
              Creează un cont gratuit pentru a salva progresul tău
            </p>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Auth Form */}
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-primary mb-4">
                  <CheckCircle className="w-5 h-5" />
                  <h3 className="text-lg font-semibold">
                    Salvează datele completate
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Mai ai doar <strong>2 pași</strong> de completat pentru
                  raportul final: alegerea serviciilor prestate și datele
                  tehnice. Înregistrează-te acum pentru a-ți salva progresul și
                  a continua mai târziu.
                </p>
              </div>

              <CustomAuthForm />

              <div className="text-center pt-2">
                <Button
                  variant="ghost"
                  onClick={onClose}
                  size="sm"
                  className="text-sm"
                >
                  Înapoi la evaluare
                </Button>
              </div>
            </div>

            {/* Right Column - Report Preview */}
            <div className="space-y-3">
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Preview Raport
                </h3>
                <p className="text-xs text-muted-foreground">
                  Iată cum arată datele tale până acum:
                </p>
              </div>

              <ReportPreviewCard />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
